import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchCmdbCards, saveBom } from '../../lib/api';
import type { BomComponent, BomSaveResponse, CmdbCardRow } from '../../lib/types';
import {
  catalogField, catalogManufacturers, changeSummary, equalVersions, filterCatalog, kindLabel,
  NO_CHANGES, replacedEntries, resultingIds, saveRequest, summaryText,
  type BomRow, type CatalogFilter, type PendingChanges,
} from './bomModel';
import { productVersionLabel } from './productCatalogModel';

/** How many catalog hits are listed before the operator has to narrow the search. */
const CATALOG_LIMIT = 50;

interface Props {
  row: BomRow;
  /** All overview rows, to point out a version with the same Stückliste. */
  rows: readonly BomRow[];
  onClose: () => void;
  /** Called after every save; the overview reloads and passes the fresh row back in. */
  onSaved: (result: BomSaveResponse) => Promise<void>;
}

function componentText(component: { name: string; version: string }): string {
  return `${component.name}${component.version ? ` ${component.version}` : ''}`;
}

/** Collects Stückliste changes of one version and saves them in one request (DEV-114). */
export default function BomEditor({ row, rows, onClose, onSaved }: Props): ReactNode {
  const { t } = useLingui();
  const [catalogRows, setCatalogRows] = useState<CmdbCardRow[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CatalogFilter>({ search: '', part: '', manufacturer: '' });
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [changes, setChanges] = useState<PendingChanges>(NO_CHANGES);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [failed, setFailed] = useState<BomSaveResponse['errors']>([]);

  useEffect(() => {
    let cancelled = false;
    fetchCmdbCards('KomponenteVersion')
      .then((response) => { if (!cancelled) setCatalogRows(response.rows); })
      .catch((caught) => { if (!cancelled) setCatalogError(errorMessage(caught)); });
    return () => { cancelled = true; };
  }, []);

  const catalog = useMemo(() => new Map((catalogRows ?? []).map((entry) => [entry.id, entry])), [catalogRows]);
  const current = row.components;
  const currentIds = useMemo(() => new Set(current.map((c) => c.component_version_id)), [current]);
  const pendingIds = useMemo(() => new Set(changes.add.map((a) => a.id)), [changes.add]);
  const result = useMemo(() => resultingIds(current, changes, catalog), [current, changes, catalog]);
  const request = useMemo(() => saveRequest(current, result), [current, result]);
  const summary = changeSummary(current, changes, catalog);
  const dirty = request.add.length > 0 || request.remove.length > 0;
  const equals = dirty ? equalVersions(rows, row, result) : [];
  const manufacturers = useMemo(() => catalogManufacturers(catalogRows ?? []), [catalogRows]);
  const hits = useMemo(() => filterCatalog(catalogRows ?? [], filter), [catalogRows, filter]);
  const needsConfirmation = !row.custom && row.instanceCount > 0;

  // Which pending addition replaces a current entry (shown on that entry).
  const replacements = new Map<number, CmdbCardRow>();
  for (const addition of changes.add) {
    const added = catalog.get(addition.id);
    if (!added || addition.keepBoth) continue;
    for (const replaced of replacedEntries(current, added)) replacements.set(replaced.component_version_id, added);
  }

  // A pending addition that replaces (or, with "beide behalten", sits beside) a current entry.
  const replacingTarget = new Map<number, number>();
  for (const addition of changes.add) {
    const added = catalog.get(addition.id);
    const first = added ? replacedEntries(current, added)[0] : undefined;
    if (first) replacingTarget.set(addition.id, first.component_version_id);
  }
  const replacingIds = new Set(replacingTarget.keys());

  const update = (next: PendingChanges): void => {
    setChanges(next);
    setConfirming(false);
  };
  const toggleRemove = (id: number): void => update({
    ...changes,
    remove: changes.remove.includes(id) ? changes.remove.filter((r) => r !== id) : [...changes.remove, id],
  });
  const undoAdd = (id: number): void => update({ ...changes, add: changes.add.filter((a) => a.id !== id) });
  const setKeepBoth = (id: number, keepBoth: boolean): void => update({
    ...changes, add: changes.add.map((a) => (a.id === id ? { ...a, keepBoth } : a)),
  });
  const addSelected = (): void => {
    update({ ...changes, add: [...changes.add, ...[...selected].map((id) => ({ id, keepBoth: false }))] });
    setSelected(new Set());
  };
  const toggleSelected = (id: number): void => setSelected((now) => {
    const next = new Set(now);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async (): Promise<void> => {
    if (!dirty) return;
    if (needsConfirmation && !confirming) { setConfirming(true); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const response = await saveBom(row.version.id, request);
      setFailed(response.errors);
      setChanges(NO_CHANGES);
      setConfirming(false);
      await onSaved(response);
    } catch (caught) {
      setSaveError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const failedLabel = (id: number): string => {
    const entry = catalog.get(id);
    return entry ? componentText({ name: catalogField(entry, 'Bezeichnung') || entry.label, version: catalogField(entry, 'Version') }) : `#${id}`;
  };

  const currentItem = (component: BomComponent): ReactNode => {
    const id = component.component_version_id;
    const removing = changes.remove.includes(id);
    const replacement = replacements.get(id);
    const replacementVersion = replacement ? catalogField(replacement, 'Version') || replacement.label : '';
    const componentLabel = componentText(component);
    return (
      <li key={id} className={`bom-edit-item${removing || replacement ? ' is-leaving' : ''}`}>
        <div className="bom-edit-text">
          <strong>{component.name}</strong>
          <span className="mono">{component.version || t`Version nicht angegeben`}</span>
          {component.manufacturer ? <span className="muted">{component.manufacturer}</span> : null}
        </div>
        {replacement ? (
          <Chip tone="warn"><Trans>Wird ersetzt durch {replacementVersion}</Trans></Chip>
        ) : removing ? (
          <><Chip tone="alert"><Trans>Wird entfernt</Trans></Chip>
            <button type="button" className="small" disabled={saving} onClick={() => toggleRemove(id)}><Trans>Zurücknehmen</Trans></button></>
        ) : (
          <button type="button" className="small" disabled={saving} onClick={() => toggleRemove(id)}
            aria-label={t`${componentLabel} entfernen`}><Trans>Entfernen</Trans></button>
        )}
      </li>
    );
  };

  const pendingItem = (id: number, keepBoth: boolean): ReactNode => {
    const entry = catalog.get(id);
    if (!entry) return null;
    const others = replacedEntries(current, entry);
    const name = catalogField(entry, 'Bezeichnung') || entry.label;
    const replacedVersions = others.map((o) => o.version || o.name).join(', ');
    return (
      <li key={`add-${id}`} className="bom-edit-item is-new">
        <div className="bom-edit-text">
          <strong>{name}</strong>
          <span className="mono">{catalogField(entry, 'Version') || t`Version nicht angegeben`}</span>
          {catalogField(entry, 'Hersteller') ? <span className="muted">{catalogField(entry, 'Hersteller')}</span> : null}
        </div>
        <Chip tone="good">{others.length && !keepBoth ? t`Ersetzt ${replacedVersions}` : t`Neu`}</Chip>
        {others.length ? (
          <label className="check bom-keep-both">
            <input type="checkbox" checked={keepBoth} disabled={saving} onChange={(event) => setKeepBoth(id, event.target.checked)} />
            <Trans>Beide behalten</Trans>
          </label>
        ) : null}
        <button type="button" className="small" disabled={saving} onClick={() => undoAdd(id)}><Trans>Zurücknehmen</Trans></button>
      </li>
    );
  };

  const { productName, instanceCount } = row;
  const versionLabel = productVersionLabel(row.version);
  const title = t`Stückliste bearbeiten · ${productName} ${versionLabel}`;
  const instanceLabel = String(row.version.display.ProductInstanceID ?? '').trim();
  const equalVersionLabels = equals.map((e) => productVersionLabel(e.version)).join(', ');
  const failedList = failed.map((f) => {
    const label = failedLabel(f.component_version_id);
    const { message } = f;
    return f.action === 'add' ? t`${label} (hinzufügen: ${message})` : t`${label} (entfernen: ${message})`;
  }).join('; ');
  const hiddenHits = hits.length - CATALOG_LIMIT;
  const selectedCount = selected.size;

  return (
    <Modal title={title} subtitle={t`${plural(current.length, { one: '# Komponente', other: '# Komponenten' })}`} size="wide"
      dismissable={!saving} onClose={onClose}
      footer={<>
        <button type="button" onClick={onClose} disabled={saving}>{dirty ? t`Abbrechen` : t`Schließen`}</button>
        <span className="bom-edit-summary" aria-live="polite">{summaryText(summary)}</span>
        <button type="button" className="primary" disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? <Spinner label={t`Speichert…`} /> : confirming ? t`Ja, speichern` : t`Speichern`}
        </button>
      </>}>
      <div className="bom-editor">
        {row.custom ? (
          <Banner kind="info">{instanceLabel
            ? <Trans>Kundenversion der Produktinstanz {instanceLabel}. Änderungen betreffen nur diese Instanz.</Trans>
            : <Trans>Kundenversion. Änderungen betreffen nur diese Instanz.</Trans>}</Banner>
        ) : row.instanceCount > 0 ? (
          <Banner kind={confirming ? 'warn' : 'info'}>
            {t`${plural(instanceCount, {
              one: 'Diese Änderung betrifft # Produktinstanz, die auf dieser Version läuft.',
              other: 'Diese Änderung betrifft # Produktinstanzen, die auf dieser Version laufen.',
            })}`}
            {confirming ? <> <Trans>Zum Speichern bitte erneut bestätigen.</Trans></> : null}
          </Banner>
        ) : null}
        {equals.length ? (
          <Banner kind="info"><Trans>Die neue Stückliste entspricht Version {equalVersionLabels}.</Trans></Banner>
        ) : null}
        {saveError ? <Banner kind="error">{saveError}</Banner> : null}
        {failed.length ? (
          <Banner kind="error" sub={t`Die übrigen Änderungen wurden gespeichert.`}>
            <Trans>Nicht gespeichert: {failedList}</Trans>
          </Banner>
        ) : null}

        <section className="bom-edit-section" aria-labelledby="bom-current-title">
          <h3 id="bom-current-title"><Trans>Aktuelle Stückliste</Trans></h3>
          {current.length || changes.add.length ? (
            <ul className="bom-edit-list">
              {/* New entries first; a replacing one sits directly under the version it replaces. */}
              {changes.add.filter((addition) => !replacingIds.has(addition.id))
                .map((addition) => pendingItem(addition.id, addition.keepBoth))}
              {current.flatMap((component) => [
                currentItem(component),
                ...changes.add.filter((addition) => replacingIds.has(addition.id)
                  && replacingTarget.get(addition.id) === component.component_version_id)
                  .map((addition) => pendingItem(addition.id, addition.keepBoth)),
              ])}
            </ul>
          ) : <p className="muted"><Trans>Noch keine Komponenten zugeordnet.</Trans></p>}
        </section>

        <section className="bom-edit-section" aria-labelledby="bom-add-title">
          <div className="bom-edit-section-head">
            <h3 id="bom-add-title"><Trans>Komponenten hinzufügen</Trans></h3>
            <Link to="/stammdaten?klasse=KomponenteType" onClick={onClose}><Trans>Komponente fehlt? Zum Komponentenkatalog</Trans></Link>
          </div>
          <div className="table-toolbar bom-catalog-toolbar">
            <input type="search" value={filter.search} placeholder={t`Name, Hersteller, Version, PURL oder CPE…`}
              aria-label={t`Komponentenkatalog durchsuchen`} onChange={(event) => setFilter({ ...filter, search: event.target.value })} />
            <select value={filter.part} aria-label={t`Nach Typ filtern`} onChange={(event) => setFilter({ ...filter, part: event.target.value })}>
              <option value=""><Trans>Alle Typen</Trans></option><option value="a">{kindLabel('a')}</option><option value="o">{kindLabel('o')}</option><option value="h">{kindLabel('h')}</option>
            </select>
            <select value={filter.manufacturer} aria-label={t`Nach Hersteller filtern`} onChange={(event) => setFilter({ ...filter, manufacturer: event.target.value })}>
              <option value=""><Trans>Alle Hersteller</Trans></option>
              {manufacturers.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          {catalogError ? <Banner kind="error">{catalogError}</Banner> : catalogRows === null ? <Spinner label={t`Lädt Komponentenkatalog…`} /> : (
            <>
              <ul className="bom-catalog-list" aria-label={t`Treffer im Komponentenkatalog`}>
                {hits.slice(0, CATALOG_LIMIT).map((entry) => {
                  const assigned = currentIds.has(entry.id) && !changes.remove.includes(entry.id);
                  const pending = pendingIds.has(entry.id);
                  const id = `bom-catalog-${entry.id}`;
                  return (
                    <li key={entry.id} className={assigned || pending ? 'is-disabled' : undefined}>
                      <input id={id} type="checkbox" checked={selected.has(entry.id) || assigned || pending}
                        disabled={assigned || pending || saving} onChange={() => toggleSelected(entry.id)} />
                      <label htmlFor={id} className="bom-edit-text">
                        <strong>{catalogField(entry, 'Bezeichnung') || entry.label}</strong>
                        <span className="mono">{catalogField(entry, 'Version') || t`Version nicht angegeben`}</span>
                        <span className="muted">{[catalogField(entry, 'Hersteller'), kindLabel(catalogField(entry, 'Part'))].filter(Boolean).join(' · ')}</span>
                      </label>
                      {assigned ? <Chip><Trans>Bereits enthalten</Trans></Chip> : pending ? <Chip tone="good"><Trans>Vorgemerkt</Trans></Chip> : null}
                    </li>
                  );
                })}
              </ul>
              {hits.length === 0 ? <p className="muted"><Trans>Keine passenden Komponentenversionen.</Trans></p> : null}
              {hits.length > CATALOG_LIMIT ? <p className="muted"><Trans>{hiddenHits} weitere Treffer – bitte die Suche eingrenzen.</Trans></p> : null}
              <button type="button" className="bom-add-selected" disabled={selected.size === 0 || saving} onClick={addSelected}>
                {selectedCount
                  ? <Plural value={selectedCount} one="# Komponentenversion hinzufügen" other="# Komponentenversionen hinzufügen" />
                  : <Trans>Auswahl hinzufügen</Trans>}
              </button>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
}
